/**
 * R10-15 — perfis de acesso do admin no lado da SPA.
 *
 * Quem decide e o backend (`AdminAccess::Gate`): `/auth/me` devolve `admin_sections`
 * (`null` = admin sem perfil, acesso total). Este modulo so LE esse snapshot para esconder
 * do menu e das rotas o que o perfil nao libera; a API recusa do mesmo jeito (403
 * `section_forbidden`).
 *
 * As chaves espelham `AdminAccess::SectionCatalog` (medhunter-api). Modulo puro (sem JSX).
 */
import type { AdminSectionKey } from '@/types/api'

/** Configuracoes > Perfis de acesso: so admin sem perfil (gerir perfis nao e uma secao). */
export const ADMIN_PROFILES_PATH = '/admin/configuracoes/perfis-de-acesso'

/**
 * Prefixo de rota -> secao. A primeira entrada que casar vence, entao prefixos mais
 * especificos vem antes. A ordem tambem e a de `firstAllowedAdminPath` (a do menu).
 *
 * Rota FORA da tabela: admin sem perfil entra, perfil restrito nao — tela nova do admin
 * nasce fechada para os perfis ate ganhar uma linha aqui (e no mapa do backend).
 */
export const ROUTE_SECTIONS: ReadonlyArray<readonly [string, AdminSectionKey]> = [
  ['/admin/dashboard', 'dashboard'],
  ['/admin/users', 'users'],
  ['/admin/mentorships', 'mentorships'],
  ['/admin/subscriptions', 'subscriptions'],
  ['/admin/analytics', 'analytics'],
  ['/admin/revenue', 'revenue'],
  ['/admin/quizzes', 'quizzes'],
  ['/admin/courses', 'courses'],
  ['/admin/prep-courses', 'prep_courses'],
  ['/admin/course-lessons', 'prep_courses'],
  ['/admin/lesson-topic-adjustments', 'prep_courses'],
  ['/admin/provas-integra', 'provas_integra'],
  ['/admin/institutions', 'catalogs'],
  ['/admin/graduation-institutions', 'catalogs'],
  ['/admin/selection-processes', 'catalogs'],
  ['/admin/exam-deadlines', 'catalogs'],
  ['/admin/exam-processes', 'catalogs'],
  ['/admin/career-goals', 'catalogs'],
  ['/admin/specialties', 'catalogs'],
  ['/admin/sub-specialties', 'catalogs'],
  ['/admin/themes', 'catalogs'],
  ['/admin/topics', 'catalogs'],
  ['/admin/catalog-corrections', 'catalogs'],
  ['/admin/followup-videos', 'followup_videos'],
  ['/admin/video-courses', 'video_courses'],
  ['/admin/aulas-ao-vivo', 'live_sessions'],
  ['/admin/pacotes-do-instrutor', 'instructor_packages'],
  ['/admin/relatos-de-dificuldade', 'difficulty_reports'],
  ['/admin/planos', 'plans'],
  ['/admin/products', 'plans'],
  ['/admin/coupons', 'coupons'],
  ['/admin/links-de-venda', 'sales_links'],
  ['/admin/announcements', 'announcements'],
  ['/admin/documentos-legais', 'settings'],
  ['/admin/chaves-de-api', 'settings'],
  ['/admin/assistente-ia', 'settings'],
  ['/admin/configuracoes/zoom', 'settings'],
  ['/admin/configuracoes/transcritor', 'settings'],
  ['/admin/configuracoes/contrato-do-aluno', 'settings'],
  ['/admin/transcricoes', 'settings'],
]

/** So o que importa daqui: o `admin_sections` do `/auth/me`. */
interface AdminSectionsHolder {
  admin_sections?: ReadonlyArray<string> | null
}

function matches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/** A secao que a rota exige, ou `null` (fora da tabela). */
export function adminRouteSection(pathname: string): AdminSectionKey | null {
  const hit = ROUTE_SECTIONS.find(([prefix]) => matches(pathname, prefix))
  return hit ? hit[1] : null
}

/**
 * Admin sem perfil. Sem o campo no payload (API anterior ao R10-15 ou `user` velho do
 * localStorage) tambem conta como total: o proximo `/auth/me` corrige e a API recusa.
 */
export function isFullAdmin(user: AdminSectionsHolder | null | undefined): boolean {
  return !Array.isArray(user?.admin_sections)
}

export function canAccessAdminPath(user: AdminSectionsHolder | null | undefined, pathname: string): boolean {
  if (isFullAdmin(user)) return true
  // `/admin` puro so redireciona para o dashboard; quem decide e a rota de destino.
  if (pathname === '/admin' || pathname === '/admin/') return true
  if (matches(pathname, ADMIN_PROFILES_PATH)) return false

  const section = adminRouteSection(pathname)
  return section !== null && (user?.admin_sections ?? []).includes(section)
}

/**
 * R12-05 — telas antigas que sairam do menu (o conteudo novo fica em Videoaulas). A rota
 * continua valendo, mas o redirecionamento inicial so cai nelas se o perfil nao liberar
 * nenhuma outra.
 */
export const LEGACY_ADMIN_PATHS: ReadonlySet<string> = new Set(['/admin/quizzes', '/admin/courses'])

/** Primeira tela liberada, na ordem do menu; `null` quando o perfil nao libera nada. */
export function firstAllowedAdminPath(user: AdminSectionsHolder | null | undefined): string | null {
  const allowed = ROUTE_SECTIONS.filter(([prefix]) => canAccessAdminPath(user, prefix))
  const hit = allowed.find(([prefix]) => !LEGACY_ADMIN_PATHS.has(prefix)) ?? allowed[0]
  return hit ? hit[0] : null
}
